import { atom, selector } from "recoil";

import { type GetCameraKitOrganizationsQuery as GetCamkitOrganizationsQuery } from "../../camkit/organizations/api/query/getOrganizations.gql-generated";
import {
    type CameraKitOrganization,
    type MyLensesOrganization as LensCentralOrganization,
} from "../../gql-generated/types";
import { type GetOrganizationsQuery as GetLensCentralOrganizationsQuery } from "../../shared/api/queries";
import {
    getDefaultOrganizationContainer,
    makeAllContainersMap,
    makeOrganizationContainers,
    makeOrganizationsMap,
} from "../context/organizations-mappings";

export const lensCentralOrganizationsState = atom<GetLensCentralOrganizationsQuery | undefined>({
    key: "lensCentralOrganizationsState",
    default: undefined,
});

export const camkitOrganizationsState = atom<GetCamkitOrganizationsQuery | undefined>({
    key: "camkitOrganizationsState",
    default: undefined,
});

export const allOrganizationsState = atom<GetCamkitOrganizationsQuery | undefined>({
    key: "allOrganizationsState",
    default: undefined,
});

export const defaultOrganizationIdState = atom<string | undefined>({
    key: "defaultOrganizationIdState",
    default: undefined,
});

export const organizationsLoadingState = atom<boolean>({
    key: "organizationsLoadingState",
    default: true,
});

export const organizationsRefetchState = atom<number>({
    key: "organizationsRefetchState",
    default: 0,
});

export const organizationsInitializedState = atom<boolean>({
    key: "organizationsInitializedState",
    default: false,
});

export const organizationsSelector = selector({
    key: "organizationsSelector",
    get: ({ get }) => {
        const lensCentralOrgs = get(lensCentralOrganizationsState);
        const camkitOrgs = get(camkitOrganizationsState);
        const allOrgs = get(allOrganizationsState);
        const defaultOrganizationId = get(defaultOrganizationIdState);

        const organizations: LensCentralOrganization[] = [
            ...(lensCentralOrgs?.getMyLensesOrganizations.organizationsList ?? []),
        ];

        const cameraKitOrganizations: CameraKitOrganization[] =
            camkitOrgs?.getOrganizations?.organizations?.flatMap((org: CameraKitOrganization | null) =>
                org ? [org] : [],
            ) ?? [];

        const allOrganizations: CameraKitOrganization[] =
            allOrgs?.getOrganizations?.organizations?.flatMap((org: CameraKitOrganization | null) =>
                org ? [org] : [],
            ) ?? [];

        const organizationsIndex = makeOrganizationsMap(organizations);

        const organizationContainers = makeOrganizationContainers(
            organizations,
            cameraKitOrganizations,
            allOrganizations,
            defaultOrganizationId ?? "",
        );

        const containersIndex = makeAllContainersMap(organizationContainers);

        return {
            organizations,
            organizationsIndex,
            organizationContainers,
            containersIndex,
        };
    },
});

export const defaultOrganizationContainerSelector = selector({
    key: "defaultOrganizationContainerSelector",
    get: ({ get }) => {
        const { organizationContainers } = get(organizationsSelector);
        const defaultOrganizationId = get(defaultOrganizationIdState);

        return getDefaultOrganizationContainer(organizationContainers, defaultOrganizationId ?? "");
    },
});
